import { mqtt5 } from "aws-iot-device-sdk-v2";
import { toUtf8 } from "@aws-sdk/util-utf8-browser";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useInjection } from "inversify-react";
import { MQTTClientProvider } from "../services/DependencyInjection";
import RangeSlider from 'react-bootstrap-range-slider';

import {
    Chart,
    registerables
} from 'chart.js';
import 'chartjs-adapter-luxon';
import { Chart as ReactChart } from 'react-chartjs-2';
import { DateTime } from "luxon";
import { VisualizationMarkdown } from "../components/VisualizationMarkdown";

Chart.register(...registerables);

export const options = {
    animation: false,
    scales: {
        x: {
            type: 'time',
            min: DateTime.now().minus({ seconds: 20 }).toUnixInteger() * 1000,
            max: DateTime.now().toUnixInteger() * 1000,
            time: {
                unit: 'seconds',
                displayFormats: {
                    millisecond: '',
                    seconds: '',
                    minute: '',
                    hour: '',
                    day: ''
                }
            }
        },
        y: {
            min: 0,
            max: 250
        }
    }
} as any;

export const data = {
    datasets: [{
        label: 'Heart Rate',
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        data: []
    }],
} as any;

let intervalId: NodeJS.Timer | null = null;

export default function MQTTExamplePage() {
    const mqttClientProvider = useInjection<MQTTClientProvider>("MQTTClientProvider");
    const chartRef = useRef<Chart>(null);

    const [mqttClient, setMqttClient] = useState<mqtt5.Mqtt5Client | null>(null);
    const [updateRate, setUpdateRate] = useState<number>(25);
    const [rangeInSeconds, setRangeInSeconds] = useState<number>(10);
    const [burstRunning, setBurstRunning] = useState<boolean>(false);

    useEffect(() => {
        const init = async function () {
            const client: mqtt5.Mqtt5Client = await mqttClientProvider();
            setMqttClient(client);

            await client.subscribe({
                subscriptions: [
                    { qos: mqtt5.QoS.AtMostOnce, topicFilter: "hello/world/qos" }
                ]
            });

            client.on("messageReceived", (eventData: mqtt5.MessageReceivedEvent): void => {
                if (eventData.message.payload) {
                    if (chartRef.current == null) return;
                    const data_point = JSON.parse(toUtf8(new Uint8Array(eventData.message.payload as ArrayBuffer)));
                    chartRef.current!.data.datasets[0].data.push(data_point);
                }
            });
        };

        init().catch(err => console.error(err));
    }, [mqttClientProvider]);

    useEffect(() => {
        if (intervalId) {
            clearInterval(intervalId);
        }

        intervalId = setInterval(() => {
            if (chartRef.current == null) return;

            const now = DateTime.now();
            chartRef.current!.options!.scales!.x!.min = now.minus({ seconds: rangeInSeconds }).toMillis();
            chartRef.current!.options!.scales!.x!.max = now.toMillis();
            chartRef.current?.update();
        }, updateRate);
    }, [updateRate, rangeInSeconds]);

    const start = async function () {
        if (mqttClient === null) {
            console.log("No mqtt client...");
            return;
        }

        if (burstRunning) {
            console.log("Burst already running.");
            return;
        }

        setBurstRunning(true);

        // Fire a sine wave burst: 100 points at 50 ms intervals over 3 wave cycles.
        const now = DateTime.now();
        const points = 100;
        const delay = 50;
        const waves = 3;
        for (let i = 0; i < points; i++) {
            setTimeout(() => {
                mqttClient.publish({
                    qos: mqtt5.QoS.AtMostOnce,
                    topicName: "hello/world/qos",
                    payload: JSON.stringify({
                        x: now.plus({ milliseconds: i * delay }).toMillis(),
                        y: 100 + Math.sin(i / (points / waves) * Math.PI * 2) * 40,
                    }),
                });
            }, i * delay);
        }
        setTimeout(() => { setBurstRunning(false); }, (points + 1) * delay);
    };

    let buttonLabel = "Start burst";
    if (mqttClient == null) {
        buttonLabel = "Client not connected...";
    } else if (burstRunning) {
        buttonLabel = "Burst running...";
    }

    const markdown = `
The visualization stack uses [inversify-js](https://inversify.io/) and [inversify-react](https://www.npmjs.com/package/inversify-react) for dependency injection.
With it, it offers a \`MQTTClientProvider\` via \`useInjection<MQTTClientProvider>("MQTTClientProvider")\` to easily get an MQTT connection.

IoT permissions are managed through CDK (\`VisualizationStackConstruct\`). The authenticated IAM role is granted \`iot:DescribeEndpoint\` and the IoT resource policy allows \`iot:Connect\`, \`iot:Publish\`, \`iot:Subscribe\` and \`iot:Receive\`.

Here's an example where we send and at the same time receive a burst of messages and display it live using Chart.js.
`;

    return <>
        <h1>Mqtt Example</h1>
        <span style={{ float: 'right' }}>
            Time Window [s]
            <RangeSlider
                min={3}
                max={60}
                value={rangeInSeconds}
                onChange={changeEvent => setRangeInSeconds(+changeEvent.target.value)}
            />
        </span>
        <VisualizationMarkdown>{markdown}</VisualizationMarkdown>
        <Button onClick={start} disabled={mqttClient == null || burstRunning}>{buttonLabel}</Button>
        <ReactChart type='line' ref={chartRef} options={options} data={data} />
    </>;
}